class ProductGrid extends HTMLElement {
  constructor () {
    super()

    this.state = {
      root: window.theme.category ? window.theme.category.stateLayer.state : window.theme.searchResults.stateLayer.state,
    }
  }

  connectedCallback () {
    this.subscribeToEvents()
  }

  subscribeToEvents () {
    if (window.theme.searchResults) {
      window.theme.searchResults.eventBus.subscribe('searchResults:state:products:updated', (products) => {
        const productsWithRoot = products.map(product => ({ ...product, root: 'searchResults' }))

        this.render(productsWithRoot)
        this.sendKeywordDataToAnalytics(products)
      })
    }

    if (window.theme.category) {
      window.theme.category.eventBus.subscribe('category:state:products:updated', (products) => {
        const productsWithRoot = products.map(product => ({ ...product, root: 'category' }))
        this.render(productsWithRoot)
      })

      window.theme.category.eventBus.subscribe('category:shopify:products:fetched', (products) => {
        this.fallbackRender()
      })
    }
  }

  _returnPromosToHiddenContainer () {
    const gridContainer = this.productGrid
    const preRenderedPromosContainer = document.getElementById('pre-rendered-promos')
    if (!gridContainer || !preRenderedPromosContainer) {
      return
    }
    const insertedPromoElements = gridContainer.querySelectorAll('[data-inserted-promo="true"]')
    insertedPromoElements.forEach(promoElement => {
      const originalPosition = promoElement.dataset.originalPosition
      if (!originalPosition) {
        return
      }
      const originalWrapper = preRenderedPromosContainer.querySelector(`[data-promo-position="${originalPosition}"]`)
      if (originalWrapper) {
        delete promoElement.dataset.insertedPromo
        delete promoElement.dataset.originalPosition
        originalWrapper.appendChild(promoElement)
      }
    })
  }

  render (products) {
    const count = products.length
    const gridContainer = this.productGrid

    if (!gridContainer) {
      return
    }

    this._returnPromosToHiddenContainer()

    gridContainer.innerHTML = ''

    const potentialPromos = this._getPotentialPromosForCurrentPage()
    if (count === 0 && potentialPromos.length === 0) {
      gridContainer.insertAdjacentHTML('beforeend', `
        <div class="no-results">
          <p>No results found</p>
        </div>
      `)
      return
    }

    products.forEach((product) => {
      const productTemplate = this.templateProductCard
      if (!productTemplate) {
        return
      }
      const clone = document.importNode(productTemplate.content, true)
      const productCard = clone.querySelector('product-card')
      if (!productCard) {
        return
      }

      productCard.setAttribute('data-prop-data', JSON.stringify(product))
      gridContainer.appendChild(clone)
    })

    this.productGrid.querySelectorAll('product-card').forEach((productCard) => {
      const link = productCard.querySelector('[data-product-card-el="link"]')
      const productData = JSON.parse(productCard.dataset.propData)

      link.addEventListener('click', (event) => {
        if (window.theme.category) {
          window.theme.category.eventBus.publish('category:tracking:product:clicked', {
            productId: productData.productId,
            url: productData.url,
          })
        }

        if (window.theme.searchResults) {
          window.theme.searchResults.eventBus.publish('searchResults:tracking:product:clicked', {
            productId: productData.productId,
            url: productData.url,
          })
        }
      })
    })

    this._insertPromoCards()
  }

  async fallbackRender () {
    this.productGrid.innerHTML = ''
    try {
      const url = `/collections/${this.collectionHandle}?section_id=${this.fallbackSectionId}`
      const fullUrl = this.pageParam ? `${url}&page=${this.pageParam}` : url
      const response = await fetch(fullUrl)

      if (!response.ok) throw new Error('Failed to fetch fallback product grid section')
      const text = await response.text()
      const html = new DOMParser().parseFromString(text, 'text/html')
      const gridHTML = html.querySelector(`[data-product-grid-external-el="fallbackProductGrid"]`).innerHTML

      this.innerHTML = gridHTML
    } catch (error) {
      console.error('Failed to fetch swatches section:', error)
    }
  }

  sendKeywordDataToAnalytics (products) {
    const keywordData = JSON.parse(sessionStorage.getItem('autocompleteKeyword'))
    if (keywordData) {
      const { keyword, searchRequest } = keywordData

      const parsedKeyword = keyword.replace(/\n/g, '').trim()
      if (parsedKeyword !== theme.searchResults.stateLayer.state.activeTerm) return

      const searchResult = {
        products: {
          hits: products.map(product => ({
            productId: product.productId,
          })),
          total: products.length,
          size: 40,
          from: 0,
        },
      }

      nostojs(api => {
        api.recordSearch(
          'autocomplete',
          searchRequest,
          searchResult,
          { isKeyword: true },
        )
      })

      sessionStorage.removeItem('autocompleteKeyword')
    }
  }

  get productGrid () {
    return this.querySelector('.vbf-product-grid')
  }

  get templateProductCard () {
    return document.getElementById('templateProductCard')
  }

  get templateHeroCard () {
    return document.getElementById('templateHeroCard')
  }

  get fallbackSectionId () {
    return 'fallback-product-grid'
  }

  get collectionHandle () {
    return this.getAttribute('data-collection-handle')
  }

  get pageParam () {
    return new URLSearchParams(window.location.search).get('page')
  }

  get allPromosData () {
    if (!this._allPromosData) {
      const promoDataEl = document.querySelector('[data-promos]')
      try {
        this._allPromosData = promoDataEl ? JSON.parse(promoDataEl.textContent) : []
      } catch (e) {
        console.error('Failed to parse promo data', e)
        this._allPromosData = []
      }
    }
    return this._allPromosData
  }

  _getPotentialPromosForCurrentPage () {
    const currentPage = this.state.root.pagination.currentPage || 1
    const itemsPerPage = window.theme.categoryConfig.productsPerPage || 40
    const firstItemIndexOnPage = (currentPage - 1) * itemsPerPage + 1
    const lastItemIndexOnPage = currentPage * itemsPerPage
    const allData = this.allPromosData || []
    return allData.filter(p => p.position >= firstItemIndexOnPage && p.position <= lastItemIndexOnPage)
  }

  _insertPromoCards () {
    const activeFacets = this.state.root.activeFacets || []
    if (activeFacets.length > 0) {
      return
    }

    const currentPage = this.state.root.pagination.currentPage || 1
    const itemsPerPage = window.theme.categoryConfig.productsPerPage || 40
    const gridContainer = this.productGrid
    const preRenderedPromosContainer = document.getElementById('pre-rendered-promos')

    if (!gridContainer || !preRenderedPromosContainer) {
      return
    }

    const firstItemIndexOnPage = (currentPage - 1) * itemsPerPage + 1
    const lastItemIndexOnPage = currentPage * itemsPerPage

    const promoWrappersToShow = Array.from(preRenderedPromosContainer.children)
      .filter(promoWrapper => {
        if (!(promoWrapper instanceof HTMLElement) || !promoWrapper.dataset || !promoWrapper.dataset.promoPosition) return false
        const position = parseInt(promoWrapper.dataset.promoPosition, 10)
        return !isNaN(position) && position >= firstItemIndexOnPage && position <= lastItemIndexOnPage
      })
      .sort((a, b) => parseInt(a.dataset.promoPosition, 10) - parseInt(b.dataset.promoPosition, 10))

    promoWrappersToShow.forEach(promoWrapper => {
      const position = parseInt(promoWrapper.dataset.promoPosition, 10)
      const promoElement = promoWrapper.firstElementChild

      if (!promoElement) {
        return
      }

      promoElement.dataset.insertedPromo = 'true'
      promoElement.dataset.originalPosition = position

      const targetPageIndex = position - firstItemIndexOnPage
      const elementAtIndex = gridContainer.children[targetPageIndex]

      if (elementAtIndex) {
        gridContainer.insertBefore(promoElement, elementAtIndex)
      } else {
        gridContainer.appendChild(promoElement)
      }
    })
  }
}

customElements.define('product-grid', ProductGrid)
